import { useState, useEffect, useMemo } from 'react'
import { useSession } from '@/admin/hooks/use-session'
import { useNavigate } from 'react-router'
import {
  ArrowRightLeft,
  X,
  Plus,
  Building2,
  Check,
  Users,
  Package,
  FileCheck,
  Shield,
  Mail,
  Activity,
  ChevronRight,
} from 'lucide-react'
import api, { setAuthToken } from '@/admin/lib/api'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/shared/components/ui/dialog'
import { Button } from '@/shared/components/ui/button'
import { SearchInput } from '@/shared/components/ui/search-input'
import { DataTable, type DataTableColumn } from '@/admin/components/data-table'
import { StaffShell } from '@/admin/components/staff/staff-shell'
import { cn } from '@/shared/lib/utils'
import { useForm } from '@/shared/lib/forms'
import type { FormField } from '@/shared/lib/forms'
import { useFormatters } from '@/shared/lib/formatting'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

import { toastApiError } from '@/shared/lib/api-error'
interface UserResult {
  uuid: string
  email: string
  first_name: string
  last_name: string
  is_staff: boolean
}

interface ImpersonationStatus {
  is_impersonating: boolean
  hijacker: { uuid: string; email: string } | null
}

interface ProvisionResult {
  tenant: { uuid: string; name: string; slug: string }
  organisation: { uuid: string; name: string }
  admin_email: string
}

interface DashboardStats {
  totals: {
    tenants: number
    organisations: number
    users: number
    staff_users: number
    consumers: number
    products: number
    registrations: number
    warranties: number
    emails_sent: number
  }
  trends: {
    registrations_30d: number
    registrations_7d: number
    new_tenants_30d: number
    emails_30d: number
  }
  recent_activity: Array<{
    id: string
    action: string
    module: string
    description: string
    timestamp: string
    user__email: string | null
    impersonator__email: string | null
  }>
  recent_tenants: Array<{
    uuid: string
    name: string
    slug: string
    is_active: boolean
    created_at: string
  }>
}

interface TenantRow {
  uuid: string
  name: string
  slug: string
  is_active: boolean
  created_at: string
  organisation_count: number
  user_count: number
  partner_count: number
  product_count: number
  registration_count: number
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function timeAgo(iso: string, formatDate: (iso: string) => string): string {
  const diff = Date.now() - new Date(iso).getTime()
  const mins = Math.floor(diff / 60_000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  if (days < 30) return `${days}d ago`
  return formatDate(iso)
}

function actionLabel(action: string): string {
  const map: Record<string, string> = {
    impersonate: 'Impersonated user',
    provision_organisation: 'Provisioned tenant',
    create: 'Created',
    update: 'Updated',
    delete: 'Deleted',
    login: 'Logged in',
  }
  return map[action] ?? action.replace(/_/g, ' ');
}

function actionColor(action: string): string {
  switch (action) {
    case 'provision_organisation':
      return 'bg-[var(--green-bg)] text-[var(--green)]'
    case 'impersonate':
      return 'bg-[var(--status-warn-bg)] text-[var(--status-warn)]'
    case 'delete':
      return 'bg-[var(--status-error-bg)] text-[var(--status-error)]'
    default:
      return 'bg-[var(--bg-subtle)] text-[var(--ink-3)]'
  }
}

// ---------------------------------------------------------------------------
// Stat card
// ---------------------------------------------------------------------------

function StatCard({
  label,
  value,
  icon: Icon,
  sub,
}: {
  label: string
  value: number | string
  icon: React.ComponentType<{ className?: string }>
  sub?: string
}) {
  return (
    <div
      className="flex items-start gap-4 rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-card)] p-5"
    >
      <div
        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border-[0.5px] border-[var(--green-border)] bg-[var(--green-bg)]"
      >
        <Icon className="h-4 w-4 text-[var(--green)]" />
      </div>
      <div>
        <p className="mb-1 text-[24px] leading-none text-[var(--ink)]">
          {value}
        </p>
        <p className="text-[11px] uppercase tracking-[0.1em] text-[var(--ink-3)]">
          {label}
        </p>
        {sub && (
          <p className="mt-1 text-[11px] text-[var(--green)]">
            {sub}
          </p>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function StaffDashboardPage() {
  const { t } = useTranslation()
  const { data: session } = useSession()
  const navigate = useNavigate()
  const { formatDate } = useFormatters()

  // Dashboard data
  const [stats, setStats] = useState<DashboardStats | null>(null)
  const [tenants, setTenants] = useState<TenantRow[]>([])
  const [tenantSearch, setTenantSearch] = useState('')

  // Impersonation
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState<UserResult[]>([])
  const [searching, setSearching] = useState(false)
  const [impersonationStatus, setImpersonationStatus] =
    useState<ImpersonationStatus | null>(null)

  // Create org modal
  const [showCreateOrg, setShowCreateOrg] = useState(false)
  const [provisioning, setProvisioning] = useState(false)
  const [provisionResult, setProvisionResult] = useState<ProvisionResult | null>(null)

  const user = session?.user

  // Load dashboard data
  useEffect(() => {
    api.get('/staff/dashboard/').then((res) => setStats(res.data)).catch(() => {})
    api
      .get('/staff/tenants/', { params: { page_size: 200 } })
      .then((res) => setTenants(res.data?.results ?? []))
      .catch(() => {})
    api.get('/staff/impersonate/status/').then((res) => setImpersonationStatus(res.data)).catch(() => {})
  }, [])

  // User search for impersonation - server-side via standard ?search=
  useEffect(() => {
    if (searchQuery.length < 2) {
      setSearchResults([])
      return
    }
    const timeout = setTimeout(async () => {
      setSearching(true)
      try {
        const res = await api.get('/staff/users/', {
          params: { search: searchQuery, page_size: 20 },
        })
        setSearchResults(res.data?.results ?? [])
      } catch {
        setSearchResults([])
      } finally {
        setSearching(false)
      }
    }, 300)
    return () => clearTimeout(timeout)
  }, [searchQuery])

  // Server-side search via ?search= when the user filters
  useEffect(() => {
    const handle = setTimeout(() => {
      api
        .get('/staff/tenants/', {
          params: {
            page_size: 200,
            ...(tenantSearch.trim() ? { search: tenantSearch.trim() } : {}),
          },
        })
        .then((res) => setTenants(res.data?.results ?? []))
        .catch(() => {})
    }, 200)
    return () => clearTimeout(handle)
  }, [tenantSearch])

  const filteredTenants = tenants

  // Actions
  async function handleImpersonate(targetUuid: string) {
    try {
      const res = await api.post(`/staff/impersonate/${targetUuid}/`)
      if (res.data.session_token) setAuthToken(res.data.session_token)
      toast.success(`Now impersonating ${res.data.user.email}`)
      window.location.href = '/dashboard'
    } catch (err: unknown) {
      toastApiError(err, 'Failed to impersonate');
    }
  }

  async function handleReleaseImpersonation() {
    try {
      const res = await api.post('/staff/impersonate/release/')
      if (res.data.session_token) setAuthToken(res.data.session_token)
      toast.success('Impersonation released')
      window.location.href = '/staff/dashboard'
    } catch (err) {
      toastApiError(err, 'Failed to release impersonation');
    }
  }

  function resetCreateOrgForm() {
    setProvisionResult(null)
    setProvisioning(false)
  }

  function handleCloseCreateOrg() {
    setShowCreateOrg(false)
    setTimeout(resetCreateOrgForm, 200)
  }

  const createOrgFields = useMemo<FormField[]>(
    () => [
      {
        type: 'text',
        name: 'organisation_name',
        label: 'Organisation name',
        placeholder: 'Acme Corporation',
        value: '',
        autoFocus: true,
        required: 'Organisation name is required',
        className: 'col-span-2',
      },
      {
        type: 'email',
        name: 'admin_email',
        label: 'Admin email',
        placeholder: 'admin@acme.com',
        value: '',
        required: 'Enter a valid email',
        className: 'col-span-2',
      },
      {
        type: 'text',
        name: 'admin_first_name',
        label: 'First name',
        value: '',
      },
      {
        type: 'text',
        name: 'admin_last_name',
        label: 'Last name',
        value: '',
      },
    ],
    [],
  )
  const { Form: CreateOrgForm, ref: createOrgFormRef } = useForm(
    {
      name: 'staff-create-org-form',
      submit: 'footer',
      fields: createOrgFields,
    },
    async (rawValues) => {
      const values = rawValues as Record<string, string>
      setProvisioning(true)
      try {
        const res = await api.post('/staff/provision-organisation/', {
          organisation_name: values.organisation_name.trim(),
          admin_email: values.admin_email.trim().toLowerCase(),
          admin_first_name: values.admin_first_name.trim(),
          admin_last_name: values.admin_last_name.trim(),
        })
        setProvisionResult(res.data)
        api.get('/staff/dashboard/').then((r) => setStats(r.data)).catch(() => {})
        api
          .get('/staff/tenants/', { params: { page_size: 200 } })
          .then((r) => setTenants(r.data?.results ?? []))
          .catch(() => {})
        toast.success(`Organisation "${values.organisation_name.trim()}" created`)
      } catch (err: unknown) {
        toastApiError(err, 'Failed to create organisation');
      } finally {
        setProvisioning(false)
      }
    },
    [showCreateOrg],
  )
  const totals = stats?.totals
  const trends = stats?.trends

  const tenantColumns: DataTableColumn<TenantRow>[] = [
    {
      id: 'name',
      header: 'Tenant',
      cell: (row) => (
        <div className="flex items-center gap-2.5">
          <div
            className={cn(
              'flex h-7 w-7 shrink-0 items-center justify-center rounded-[8px] border-[0.5px] text-[10px] font-medium',
              row.is_active
                ? 'border-[var(--green-border)] bg-[var(--green-bg)] text-[var(--green)]'
                : 'border-[var(--rule)] bg-[var(--bg-subtle)] text-[var(--ink-3)]'
            )}
          >
            {row.name.charAt(0).toUpperCase()}
          </div>
          <div>
            <p className="leading-tight text-[var(--ink)]">
              {row.name}
            </p>
            <p className="text-[10px] font-mono text-[var(--ink-3)]">
              {row.slug}
            </p>
          </div>
        </div>
      ),
    },
    {
      id: 'user_count',
      header: 'Users',
      cell: (row) => (
        <span className="text-right block">{row.user_count}</span>
      ),
    },
    {
      id: 'partner_count',
      header: 'Partners',
      cell: (row) => (
        <span className="text-right block">{row.partner_count}</span>
      ),
    },
    {
      id: 'product_count',
      header: 'Products',
      cell: (row) => (
        <span className="text-right block">{row.product_count}</span>
      ),
    },
    {
      id: 'registration_count',
      header: 'Regs',
      cell: (row) => (
        <span className="text-right block">{row.registration_count}</span>
      ),
    },
    {
      id: 'created_at',
      header: 'Created',
      cell: (row) => (
        <span className="block text-right text-[var(--ink-3)]">
          {formatDate(row.created_at)}
        </span>
      ),
    },
  ]

  return (
    <>
      {impersonationStatus?.is_impersonating && (
        <div className="flex h-8 items-center justify-center gap-3 bg-[var(--status-warn-bg)] text-[11px] uppercase tracking-[0.1em] text-[var(--status-warn)]">
          <ArrowRightLeft className="h-3 w-3" />
          {t('staffDashboard.impersonating', { email: user?.email })}
          <Button variant="link" onClick={handleReleaseImpersonation} className="underline cursor-pointer">
            {t('common.release')}
          </Button>
        </div>
      )}
      <StaffShell>
      <div className="max-w-6xl mx-auto px-6 py-10">
        {/* Title row */}
        <div className="flex items-start justify-between mb-10">
          <div>
            <h1 className="clara-title mb-2">{t('staffDashboard.title')}</h1>
            <p className="clara-small">{t('staffDashboard.description')}</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate('/staff/onboarding/new')}
              className="flex items-center gap-2 px-4 py-2.5 rounded-[4px] text-[11px] uppercase tracking-[0.1em] transition-all duration-150 cursor-pointer"
              style={{ background: 'var(--green)', color: 'var(--bg)' }}
              onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--green-hover)')}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'var(--green)')}
            >
              <Plus className="h-3.5 w-3.5" />
              New Client
            </button>
            <button
              onClick={() => setShowCreateOrg(true)}
              className="flex items-center gap-2 px-4 py-2.5 rounded-[4px] text-[11px] uppercase tracking-[0.1em] transition-all duration-150 cursor-pointer"
              style={{ border: '0.5px solid var(--rule)', color: 'var(--ink-2)' }}
              onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--bg-subtle)')}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
            >
              <Plus className="h-3.5 w-3.5" />
              New Organisation
            </button>
          </div>
        </div>

        {/* ── Stats Grid ──────────────────────────────────────── */}
        <section className="mb-10">
          <h3
            className="clara-section-label mb-5 border-b-[0.5px] border-[var(--rule)] pb-3"
          >
            {t('staffDashboard.platformOverview')}
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            <StatCard
              label="Tenants"
              value={totals?.tenants ?? '-'}
              icon={Building2}
              sub={trends?.new_tenants_30d ? `+${trends.new_tenants_30d} this month` : undefined}
            />
            <StatCard label="Users" value={totals?.users ?? '-'} icon={Users} />
            <StatCard label="Products" value={totals?.products ?? '-'} icon={Package} />
            <StatCard
              label="Registrations"
              value={totals?.registrations ?? '-'}
              icon={FileCheck}
              sub={trends?.registrations_7d ? `+${trends.registrations_7d} this week` : undefined}
            />
            <StatCard label="Warranties" value={totals?.warranties ?? '-'} icon={Shield} />
          </div>
          {/* Secondary stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
            <div
              className="flex cursor-pointer items-center gap-3 rounded-xl bg-[var(--bg-subtle)] px-4 py-3 transition-colors hover:bg-[var(--bg-card)]"
              onClick={() => navigate('/staff/consumers')}
            >
              <Users className="h-3.5 w-3.5 text-[var(--ink-3)]" />
              <span className="text-[13px] text-[var(--ink)]">
                {totals?.consumers ?? 0}
              </span>
              <span className="text-[11px] text-[var(--ink-3)]">consumers</span>
            </div>
            <div className="flex items-center gap-3 rounded-xl bg-[var(--bg-subtle)] px-4 py-3">
              <Users className="h-3.5 w-3.5 text-[var(--ink-3)]" />
              <span className="text-[13px] text-[var(--ink)]">
                {totals?.staff_users ?? 0}
              </span>
              <span className="text-[11px] text-[var(--ink-3)]">staff</span>
            </div>
            <div className="flex items-center gap-3 rounded-xl bg-[var(--bg-subtle)] px-4 py-3">
              <Mail className="h-3.5 w-3.5 text-[var(--ink-3)]" />
              <span className="text-[13px] text-[var(--ink)]">
                {totals?.emails_sent ?? 0}
              </span>
              <span className="text-[11px] text-[var(--ink-3)]">emails sent</span>
            </div>
            <div className="flex items-center gap-3 rounded-xl bg-[var(--bg-subtle)] px-4 py-3">
              <Building2 className="h-3.5 w-3.5 text-[var(--ink-3)]" />
              <span className="text-[13px] text-[var(--ink)]">
                {totals?.organisations ?? 0}
              </span>
              <span className="text-[11px] text-[var(--ink-3)]">organisations</span>
            </div>
          </div>
        </section>

        {/* ── Two-column: Tenants + Activity ───────────────── */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 mb-10">
          {/* Tenant table - 3 cols */}
          <section className="lg:col-span-3">
            <div className="mb-5 flex items-center justify-between border-b-[0.5px] border-[var(--rule)] pb-3">
              <h3 className="clara-section-label">Tenants</h3>
              <div className="w-56">
                <SearchInput
                  value={tenantSearch}
                  onChange={setTenantSearch}
                  placeholder="Search tenants…"
                  className="w-full rounded-[2px] border-[0.5px] border-[var(--rule)] bg-[var(--bg)] py-1.5 pr-3 text-[12px] text-[var(--ink)]"
                />
              </div>
            </div>
            <DataTable<TenantRow>
              tableId="staff-tenants"
              columns={tenantColumns}
              data={filteredTenants}
              onRowClick={(row) => navigate(`/staff/tenants/${row.uuid}`)}
              emptyMessage={tenantSearch ? 'No tenants match your search' : 'No tenants yet'}
            />
          </section>

          {/* Activity feed - 2 cols */}
          <section className="lg:col-span-2">
            <h3
              className="clara-section-label mb-5 border-b-[0.5px] border-[var(--rule)] pb-3"
            >
              <Activity className="mr-2 inline-block h-3.5 w-3.5 -mt-0.5 text-[var(--ink-3)]" />
              Recent Activity
            </h3>
            <div className="flex flex-col gap-1">
              {stats?.recent_activity.map((entry) => {
                const colors = actionColor(entry.action)
                return (
                  <div
                    key={entry.id}
                    className="flex items-start gap-3 px-3 py-2.5 rounded-[8px] transition-colors duration-100 hover:bg-[var(--bg-subtle)]"
                  >
                    <div
                      className={cn(
                        'mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-md',
                        colors
                      )}
                    >
                      <ChevronRight className="h-3 w-3" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-[12px] leading-snug text-[var(--ink)]">
                        {entry.description || actionLabel(entry.action)}
                      </p>
                      <p className="mt-0.5 text-[10px] text-[var(--ink-3)]">
                        {timeAgo(entry.timestamp, formatDate)}
                        {entry.impersonator__email && (
                          <span> · by {entry.impersonator__email}</span>
                        )}
                      </p>
                    </div>
                  </div>
                );
              })}
              {(!stats?.recent_activity || stats.recent_activity.length === 0) && (
                <p className="px-3 py-4 text-[12px] text-[var(--ink-3)]">
                  No recent activity
                </p>
              )}
            </div>
          </section>
        </div>

        {/* ── Impersonation ───────────────────────────────────── */}
        <section>
          <h3
            className="clara-section-label mb-5 border-b-[0.5px] border-[var(--rule)] pb-3"
          >
            {t('staffDashboard.impersonateUser')}
          </h3>
          <div className="relative max-w-md">
            <SearchInput
              value={searchQuery}
              onChange={setSearchQuery}
              placeholder={t('staffDashboard.searchPlaceholder')}
              className="w-full rounded-[2px] border-[0.5px] border-[var(--rule-strong)] bg-[var(--bg)] py-2.5 pr-8 text-[13px] text-[var(--ink)] transition-all duration-150 focus-visible:border-[var(--green)]"
            />
            {searchQuery && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer text-[var(--ink-3)]"
              >
                <X className="h-3.5 w-3.5" />
              </Button>
            )}
          </div>

          {searchResults.length > 0 && (
            <div
              className="mt-2 max-w-md max-h-[320px] overflow-y-auto rounded-[4px] border-[0.5px] border-[var(--rule)]"
            >
              {searchResults.map((u) => (
                <div
                  key={u.uuid}
                  className="flex cursor-pointer items-center justify-between border-b-[0.5px] border-[var(--rule)] px-4 py-3 transition-all duration-150 hover:bg-[var(--bg-subtle)]"
                  onClick={() => !u.is_staff && handleImpersonate(u.uuid)}
                >
                  <div>
                    <p className="text-[13px] text-[var(--ink)]">
                      {u.first_name} {u.last_name}
                    </p>
                    <p className="text-[11px] text-[var(--ink-3)]">
                      {u.email}
                    </p>
                  </div>
                  {u.is_staff ? (
                    <span
                      className="rounded-full bg-[var(--bg-subtle)] px-2 py-0.5 text-[9px] uppercase tracking-[0.1em] text-[var(--ink-3)]"
                    >
                      {t('common.staff')}
                    </span>
                  ) : (
                    <span
                      className="text-[10px] uppercase tracking-[0.1em] text-[var(--green)]"
                    >
                      Impersonate →
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
          {searching && (
            <p className="clara-small mt-2">{t('staffDashboard.searching')}</p>
          )}
          {searchQuery.length >= 2 && !searching && searchResults.length === 0 && (
            <p className="clara-small mt-2">{t('staffDashboard.noUsersFound')}</p>
          )}
        </section>
      </div>
      {/* ── Create Organisation Modal ─────────────────────── */}
      <Dialog
        open={showCreateOrg}
        onOpenChange={(open) => {
          if (!open) {
            createOrgFormRef.current?.resetForm()
            handleCloseCreateOrg()
          }
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {provisionResult ? 'Organisation created' : 'New Organisation'}
            </DialogTitle>
          </DialogHeader>

          {provisionResult ? (
            <div className="py-4">
              <div
                className="mb-4 flex items-center gap-3 rounded-[8px] border-[0.5px] border-[var(--green-border)] bg-[var(--green-bg)] p-4"
              >
                <div
                  className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[var(--green)] text-[var(--bg)]"
                >
                  <Check className="h-4 w-4" />
                </div>
                <div>
                  <p className="text-[13px] text-[var(--ink)]">
                    {provisionResult.tenant.name}
                  </p>
                  <p className="text-[11px] text-[var(--ink-3)]">
                    Invitation sent to {provisionResult.admin_email}
                  </p>
                </div>
              </div>
              <div className="flex flex-col gap-2">
                <div className="flex items-baseline justify-between gap-4">
                  <span className="text-[11px] text-[var(--ink-3)]">Tenant</span>
                  <span className="text-[13px] text-[var(--ink)]">{provisionResult.tenant.name}</span>
                </div>
                <div className="flex items-baseline justify-between gap-4">
                  <span className="text-[11px] text-[var(--ink-3)]">Slug</span>
                  <span className="text-[13px] font-mono text-[var(--ink)]">{provisionResult.tenant.slug}</span>
                </div>
                <div className="flex items-baseline justify-between gap-4">
                  <span className="text-[11px] text-[var(--ink-3)]">Admin</span>
                  <span className="text-[13px] text-[var(--ink)]">{provisionResult.admin_email}</span>
                </div>
              </div>
            </div>
          ) : (
            <div className="py-2 flex flex-col gap-4">
              <CreateOrgForm className="grid grid-cols-2 gap-3" />
              <div
                className="flex items-center gap-2 rounded-[4px] bg-[var(--bg-subtle)] px-3 py-2.5"
              >
                <Building2 className="h-3.5 w-3.5 shrink-0 text-[var(--ink-3)]" />
                <p className="text-[11px] text-[var(--ink-3)]">
                  The admin will receive an email invitation to set up their account.
                </p>
              </div>
            </div>
          )}

          <DialogFooter>
            <div className="flex items-center gap-2 w-full justify-end">
              {provisionResult ? (
                <Button
                  onClick={handleCloseCreateOrg}
                  className="cursor-pointer rounded-[4px] bg-[var(--green)] px-4 py-1.5 text-[11px] uppercase tracking-[0.1em] text-[var(--bg)] transition-all duration-150"
                >
                  Done
                </Button>
              ) : (
                <Button
                  type="submit"
                  form="staff-create-org-form"
                  disabled={provisioning}
                  className="cursor-pointer rounded-[4px] bg-[var(--green)] px-4 py-1.5 text-[11px] uppercase tracking-[0.1em] text-[var(--bg)] transition-all duration-150 hover:bg-[var(--green-hover)] disabled:opacity-40"
                >
                  {provisioning ? 'Creating...' : 'Create Organisation'}
                </Button>
              )}
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </StaffShell>
    </>
  );
}
